import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { resetPassword } = useAuth();
    const { theme } = useTheme();
    const location = useLocation();

    // Get email from location state if available
    useEffect(() => {
        if (location.state && location.state.email) {
            setEmail(location.state.email);
        }
    }, [location.state]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            await resetPassword(email);
            setMessage(`An email has been sent to ${email} with further instructions.`);
        } catch (error) {
            console.error('Password reset error:', error);
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Failed to reset password');
            }
        } finally {
            setLoading(false);
        }
    };

    const containerStyle = {
        maxWidth: '400px',
        margin: '40px auto',
        padding: theme.spacing.LARGE,
        backgroundColor: theme.colors.BACKGROUND,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    };

    const inputStyle = {
        width: '100%',
        padding: theme.spacing.SMALL,
        marginBottom: theme.spacing.MEDIUM,
        border: `1px solid ${theme.colors.INPUT_BORDER}`,
        borderRadius: '4px',
        fontSize: theme.fonts.MEDIUM,
        backgroundColor: theme.colors.INPUT_BACKGROUND,
        color: theme.colors.INPUT_TEXT,
    };

    const buttonStyle = {
        width: '100%',
        padding: theme.spacing.SMALL,
        backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
        color: theme.colors.BUTTON_PRIMARY_TEXT,
        border: 'none',
        borderRadius: '4px',
        fontSize: theme.fonts.MEDIUM,
        cursor: 'pointer',
        marginBottom: theme.spacing.MEDIUM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        fontWeight: '500',
        height: '40px',
    };

    const errorStyle = {
        color: theme.colors.ERROR,
        marginBottom: theme.spacing.MEDIUM,
        textAlign: 'center' as const,
        padding: theme.spacing.SMALL,
        backgroundColor: `${theme.colors.ERROR}20`,
        borderRadius: '4px',
    };

    const successStyle = {
        color: theme.colors.SUCCESS,
        marginBottom: theme.spacing.MEDIUM,
        textAlign: 'center' as const,
        padding: theme.spacing.SMALL,
        backgroundColor: `${theme.colors.SUCCESS}20`,
        borderRadius: '4px',
    };

    return (
        <div style={containerStyle}>
            <style>
                {`
          input::placeholder {
            color: rgba(0, 0, 0, 0.6) !important;
            opacity: 1;
          }
        `}
            </style>
            <h2
                style={{
                    textAlign: 'center',
                    color: theme.colors.TEXT,
                    marginBottom: theme.spacing.LARGE,
                    fontSize: '1.8em',
                }}
            >
                Reset Password
            </h2>

            {error && <div style={errorStyle}>{error}</div>}
            {message && <div style={successStyle}>{message}</div>}

            <form onSubmit={handleSubmit}>
                <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle}
                    required
                />
                <button type="submit" disabled={loading} style={buttonStyle}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
            </form>

            <div
                style={{
                    textAlign: 'center',
                    marginTop: theme.spacing.MEDIUM,
                    color: theme.colors.TEXT,
                }}
            >
                Remember your password?{' '}
                <Link
                    to="/login"
                    state={{ email: email }}
                    style={{
                        color: theme.colors.LINK,
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        opacity: 1,
                        transition: 'all 0.2s ease',
                        padding: `${theme.spacing.SMALL}px ${theme.spacing.SMALL}px`,
                        borderRadius: '4px',
                        backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        display: 'inline-block',
                        marginLeft: theme.spacing.SMALL,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                        e.currentTarget.style.color = theme.isDark ? theme.colors.LINK : theme.colors.PRIMARY;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
                        e.currentTarget.style.color = theme.colors.LINK;
                    }}
                >
                    Sign in
                </Link>
            </div>
        </div>
    );
};

export default ForgotPassword; 